import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Users, Plus, Phone, Mail, Building2, MoreHorizontal, Pencil, Trash2, Smartphone,
  ArrowUp, ArrowDown, ArrowUpDown, Copy, Download, Settings2, RefreshCw, FilterX,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDebounce } from '@/hooks/useDebounce';
import { useContacts, useCreateContact, useUpdateContact, useDeleteContact } from '../hooks/useCrmContacts';
import { getContacts } from '../api/crmApi';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import { FieldDocsTable, CONTACT_FIELDS } from '../components/FieldDocs';
import { ContactModal } from '../components/ContactModal';
import type { Contact, ContactFilters, CreateContactInput } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

// =============================================================================
// Column Definitions (built inside component to support t())
// =============================================================================

function buildColumns(t: (key: string) => string): ColumnDef<Contact>[] {
  return [
  {
    key: 'name',
    label: t('contacts.columns.name'),
    defaultVisible: true,
    sortKey: 'lastName',
    filterType: 'text',
    getValue: (c) => `${c.first_name} ${c.last_name}`,
    render: (c) => (
      <div>
        <div className="font-medium">{c.first_name} {c.last_name}</div>
        {c.is_primary && (
          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">{t('common.primaryBadge')}</Badge>
        )}
      </div>
    ),
  },
  {
    key: 'companyName',
    label: t('contacts.columns.company'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.company_name || null,
    render: (c) => c.company_name ? (
      <span className="flex items-center gap-1 text-sm">
        <Building2 className="h-3 w-3 text-muted-foreground" />
        {c.company_name}
      </span>
    ) : <span className="text-muted-foreground text-sm">{'\u2014'}</span>,
  },
  {
    key: 'title',
    label: t('contacts.columns.title'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.title || null,
  },
  {
    key: 'email',
    label: t('contacts.columns.email'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.email || null,
    render: (c) => c.email ? (
      <a
        href={`mailto:${c.email}`}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Mail className="h-3 w-3" /> {c.email}
      </a>
    ) : <span className="text-muted-foreground text-sm">{'\u2014'}</span>,
  },
  {
    key: 'phone',
    label: t('contacts.columns.phone'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.phone || c.mobile_phone || null,
    render: (c) => (
      <div className="flex flex-col gap-0.5 text-sm text-muted-foreground">
        {c.phone && (
          <span className="flex items-center gap-1">
            <Phone className="h-3 w-3" /> {c.phone}
          </span>
        )}
        {c.mobile_phone && (
          <span className="flex items-center gap-1">
            <Smartphone className="h-3 w-3" /> {c.mobile_phone}
          </span>
        )}
        {!c.phone && !c.mobile_phone && '\u2014'}
      </div>
    ),
  },
  {
    key: 'department',
    label: t('contacts.columns.department'),
    defaultVisible: true,
    filterType: 'text',
    getValue: (c) => c.department || null,
  },
  {
    key: 'actions',
    label: '',
    defaultVisible: true,
    noFilter: true,
    getValue: () => '',
    className: 'w-[50px]',
  },
  ];
}

// =============================================================================
// Persistence Helpers
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-crm-contacts-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-crm-contacts-column-visibility';

// Static key list for persistence
const COLUMN_KEYS: { key: string; defaultVisible: boolean }[] = [
  { key: 'name', defaultVisible: true },
  { key: 'companyName', defaultVisible: true },
  { key: 'title', defaultVisible: true },
  { key: 'email', defaultVisible: true },
  { key: 'phone', defaultVisible: true },
  { key: 'department', defaultVisible: true },
  { key: 'actions', defaultVisible: true },
];

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMN_KEYS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function loadColumnFilters(): Record<string, string> {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return {};
}

function saveColumnFilters(filters: Record<string, string>) {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey: string | undefined;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// CrmContactsPage
// =============================================================================

export function CrmContactsPage() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  const COLUMNS = useMemo(() => buildColumns(t), [t]);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | undefined>('lastName');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; label: string } | null>(null);

  // Column filters (persisted)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  // Persist filters
  useEffect(() => { saveColumnFilters(columnFilters); }, [columnFilters]);
  useEffect(() => { saveColumnVisibility(columnVisibility); }, [columnVisibility]);

  // ---------------------------------------------------------------------------
  // Mode B: Server promotion
  // ---------------------------------------------------------------------------

  // All filters are text -- concatenate for server search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => !!value)
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const debouncedColumnSearch = useDebounce(columnSearchText, 300);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [debouncedColumnSearch]);

  const filters: ContactFilters = {
    search: debouncedColumnSearch || undefined,
  };

  // ---------------------------------------------------------------------------
  // Queries & Mutations
  // ---------------------------------------------------------------------------
  const { data, isLoading, isError, refetch } = useContacts(filters, page, sortKey, sortDirection);
  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = 25;
  const totalPages = Math.ceil(total / pageSize) || 1;

  // ---------------------------------------------------------------------------
  // Column visibility
  // ---------------------------------------------------------------------------
  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnVisibility],
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setColumnFilters({});
    setPage(0);
  };

  const hasActiveFilters = Object.values(columnFilters).some(Boolean);

  const handleCreate = useCallback(() => {
    setEditingContact(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((contact: Contact) => {
    setEditingContact(contact);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async (input: CreateContactInput) => {
    if (editingContact) {
      await updateMutation.mutateAsync({ id: editingContact.id, ...input });
    } else {
      await createMutation.mutateAsync(input);
    }
    setShowModal(false);
    setEditingContact(null);
  }, [editingContact, createMutation, updateMutation]);

  const handleDelete = useCallback((id: string, label: string) => {
    setPendingDelete({ id, label });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
  }, [deleteMutation, pendingDelete]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const exportColumns = useMemo(
    () => visibleColumns.filter((c) => !c.noFilter),
    [visibleColumns],
  );

  const handleCopy = () => {
    copyToClipboard(items, exportColumns);
  };

  const handleCSVPage = () => {
    exportToCSV(items, exportColumns, 'crm_contacts');
  };

  const handleCSVAll = async () => {
    try {
      const allData = await getContacts(filters, 0, 10000, sortKey, sortDirection);
      exportToCSV(allData.items, exportColumns, 'crm_contacts_all');
    } catch {
      toast.error(t('contacts.failedToExport'));
    }
  };

  // ---------------------------------------------------------------------------
  // Filter rendering
  // ---------------------------------------------------------------------------
  const renderColumnFilter = (col: ColumnDef<Contact>) => {
    if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

    // Text filter (default -- contacts only have text filters)
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => setColumnFilter(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // ---------------------------------------------------------------------------
  // Cell rendering
  // ---------------------------------------------------------------------------
  const renderCell = (col: ColumnDef<Contact>, contact: Contact) => {
    if (col.key === 'actions') {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEdit(contact)}>
              <Pencil className="h-4 w-4 mr-2" /> {t('common.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => handleDelete(contact.id, `${contact.first_name} ${contact.last_name}`)}
            >
              <Trash2 className="h-4 w-4 mr-2" /> {t('common.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    if (col.render) return col.render(contact);

    const raw = col.getValue(contact);
    return <span className="text-sm text-muted-foreground">{String(raw ?? '') || '\u2014'}</span>;
  };

  // ---------------------------------------------------------------------------
  // Showing text
  // ---------------------------------------------------------------------------
  const showingText = t('common.showing', { shown: Math.min(items.length, total), total });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          <h1 className="text-xl font-semibold">{t('contacts.pageTitle')}</h1>
          {!isLoading && (
            <Badge variant="secondary" className="ml-2">{total}</Badge>
          )}
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t('contacts.addContact')}
        </Button>
      </div>

      <Tabs defaultValue="list" className="flex flex-col gap-4">
        <TabsList className="self-start">
          <TabsTrigger value="list">{t('contacts.listTab')}</TabsTrigger>
          <TabsTrigger value="documentation">{t('contacts.documentationTab')}</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="flex flex-col gap-4 mt-0">
      {/* Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('contacts.allContacts')}</CardTitle>
              <CardDescription>{showingText}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  <FilterX className="h-4 w-4 mr-1" />
                  {t('common.clearFilters')}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-1" />
                {t('common.copy')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    CSV
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleCSVPage}>{t('common.exportPage')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCSVAll}>{t('common.exportAll')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings2 className="h-4 w-4 mr-1" />
                    {t('common.columns')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t('common.toggleColumns')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={isVisible(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* ROW 1: Filter inputs */}
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  {visibleColumns.map((col) => renderColumnFilter(col))}
                </TableRow>

                {/* ROW 2: Column labels + sort controls */}
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={cn(
                        col.className,
                        col.align === 'right' && 'text-right',
                      )}
                    >
                      {col.sortKey ? (
                        <SortableHeader
                          label={col.label}
                          sortKey={col.sortKey}
                          currentSortKey={sortKey}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Loading state */}
                {isLoading && Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Error state */}
                {!isLoading && isError && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="py-8 px-4">
                      <Alert variant="destructive" className="max-w-md mx-auto">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t('contacts.failedToLoad')}</AlertTitle>
                        <AlertDescription className="mt-1 flex items-center gap-3">
                          <span>{t('common.somethingWentWrong')}</span>
                          <Button variant="outline" size="sm" onClick={() => refetch()}>
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            {t('common.retry')}
                          </Button>
                        </AlertDescription>
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}

                {/* Empty state */}
                {!isLoading && !isError && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length} className="text-center py-12 text-muted-foreground">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p>{hasActiveFilters ? t('contacts.noContactsFiltered') : t('contacts.noContacts')}</p>
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!isLoading && !isError && items.map((contact) => (
                  <TableRow
                    key={contact.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                  >
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right tabular-nums',
                          col.className,
                        )}
                      >
                        {renderCell(col, contact)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {t('common.page', { current: page + 1, total: totalPages })}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  {t('common.previous')}
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="documentation" className="mt-0">
          <FieldDocsTable fields={CONTACT_FIELDS} />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.deleteTitle', { name: pendingDelete?.label })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contacts.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Modal */}
      <ContactModal
        open={showModal}
        onOpenChange={(v) => { setShowModal(v); if (!v) setEditingContact(null); }}
        contact={editingContact}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
